import * as React from 'react';
import clsx from 'clsx';
// the app-import-free slice of `src/i18n` (see i18n-context.tsx) — same pattern as data-table's labels
import { useTranslations } from 'i18n/use-translations';

export interface ProgressProps {
    variant?: 'determinate' | 'indeterminate';
    value?: number;
    color?: 'primary' | 'secondary';
    height?: number | string;
    foregroundColor?: string;
    backgroundColor?: string;
    className?: string;
    testId?: string;
    'aria-label'?: string;
}

const BRAND_ORANGE = 'var(--data-vis-brand)';

// automatic colour for the progress background based on the foreground colour (taken from MUI's LinearProgress)
// this is not used if the caller specifies their own background colour
const lighten = (color: string): string => `color-mix(in srgb, ${color} 38%, white)`;
const clamp = (value: number): number => Math.min(100, Math.max(0, value));

export const Progress: React.FC<ProgressProps> = (props) => {
    const { variant = 'indeterminate', value = 0, color = 'primary' } = props;
    const { height, foregroundColor, backgroundColor, className, testId } = props;
    const { __ } = useTranslations();

    const determinate = variant === 'determinate';
    const barColor = foregroundColor ?? (color === 'secondary' ? 'var(--sys-secondary)' : BRAND_ORANGE);
    const barBase = 'absolute inset-y-0 left-0 origin-left';
    const barStyle: React.CSSProperties = { backgroundColor: barColor };

    return (
        <div
            role="progressbar"
            aria-label={props['aria-label'] ?? __('misc.loading')}
            aria-valuenow={determinate ? Math.round(clamp(value)) : undefined}
            aria-valuemin={determinate ? 0 : undefined}
            aria-valuemax={determinate ? 100 : undefined}
            data-testid={testId}
            className={clsx('relative block h-1 overflow-hidden', className)}
            style={{ height, backgroundColor: backgroundColor ?? lighten(barColor) }}
        >
            {determinate ? (
                <div
                    className={clsx(barBase, 'w-full transition-transform duration-200 ease-linear')}
                    style={{ ...barStyle, transform: `scaleX(${clamp(value) / 100})` }}
                />
            ) : (
                <>
                    <div className={clsx(barBase, 'animate-progress-indeterminate-1')} style={barStyle} />
                    <div className={clsx(barBase, 'animate-progress-indeterminate-2')} style={barStyle} />
                </>
            )}
        </div>
    );
};
